// Port of the iOS GaugeManager (GaugeManager.swift). Owns every gauge loaded
// this session, fetches the NWS reference ArcGIS layer for a padded region
// when no fresh cached region covers the viewport, loads NWPS status per gauge
// with bounded concurrency and a 10-minute cache, and publishes the gauges in
// the padded visible region in 500 ms batches.
import { fetchNOAAGauges } from '../api/arcgisGauges';
import { fetchGaugeSeverity, GaugeNotFoundError } from '../api/nwps';
import { METERS_PER_DEGREE_LAT } from '../config/constants';
import { GAUGE_CACHE } from '../config/gauges';
import { hoursFromNow } from '../lib/format';
import {
  distanceSquaredDeg,
  paddedRegion,
  regionContainsPoint,
  regionContainsRegion,
  scaledRegion,
  type LatLng,
  type Region,
} from '../lib/geo';
import type { Gauge, GaugeSeverityResult } from '../types/gauges';

interface CachedGauge {
  gauge: Gauge;
  severityUpdatedAt: number | null;
  /** When NWPS last answered 404 for this site; such sites are skipped for notFoundLifetimeMs. */
  notFoundAt: number | null;
}

interface CachedRegion {
  region: Region;
  loadedAt: number;
}

interface LoadRequest {
  center: LatLng;
  distance: number;
}

type Listener = () => void;

export class GaugeStore {
  private cachedGaugesByID = new Map<string, CachedGauge>();
  private cachedRegions: CachedRegion[] = [];
  private visibleRegion: Region | null = null;
  private pendingLoadRequest: LoadRequest | null = null;
  private lastLoadRequest: LoadRequest | null = null;
  private loading = false;
  private loadAbort: AbortController | null = null;
  private severityTasksByID = new Map<string, AbortController>();
  private pendingSeverityIDs: string[] = [];
  private publishTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<Listener>();

  /** Cached gauges inside the padded visible region, hours precomputed, sorted by id. */
  visibleGauges: Gauge[] = [];
  /** True while an ArcGIS region fetch is in flight. */
  isLoadingRegion = false;

  constructor(
    private readonly loadRegion: (region: Region, signal: AbortSignal) => Promise<Gauge[]> = fetchNOAAGauges,
    private readonly loadSeverity: (id: string, signal: AbortSignal) => Promise<GaugeSeverityResult> = fetchGaugeSeverity,
    private readonly now: () => number = Date.now,
  ) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Gauge[] => this.visibleGauges;

  private notify() {
    for (const l of this.listeners) l();
  }

  clear(): void {
    this.loadAbort?.abort();
    this.loadAbort = null;
    this.pendingLoadRequest = null;
    for (const ac of this.severityTasksByID.values()) ac.abort();
    this.severityTasksByID.clear();
    this.pendingSeverityIDs = [];
    if (this.publishTimer) clearTimeout(this.publishTimer);
    this.publishTimer = null;
    this.cachedGaugesByID.clear();
    this.cachedRegions = [];
    this.visibleGauges = [];
    this.notify();
  }

  /** Updates the on-screen region used to cull gauges and republishes from the cache. */
  updateVisibleRegion(region: Region): void {
    this.visibleRegion = region;
    this.publishCachedGauges();
  }

  /** Coalesces camera changes into one active load plus the latest pending viewport. */
  requestLoad(center: LatLng, distance: number): void {
    this.pendingLoadRequest = { center, distance };
    this.lastLoadRequest = this.pendingLoadRequest;
    if (this.loading) return;
    void this.processPendingLoads();
  }

  /** Re-prioritizes stale status loads for the last viewport (used when the tab becomes visible again). */
  refresh(): void {
    if (this.lastLoadRequest) this.requestLoad(this.lastLoadRequest.center, this.lastLoadRequest.distance);
  }

  private async processPendingLoads(): Promise<void> {
    this.loading = true;
    try {
      while (this.pendingLoadRequest) {
        const request = this.pendingLoadRequest;
        this.pendingLoadRequest = null;
        await this.loadGauges(request.center, request.distance);
      }
    } finally {
      this.loading = false;
    }
  }

  /** iOS visibleSpan(for:): max(1 degree, distance / 111,000 * 1.5). */
  static visibleRegion(center: LatLng, distance: number): Region {
    const span = Math.max(1.0, (distance / METERS_PER_DEGREE_LAT) * 1.5);
    return { center, latDelta: span, lonDelta: span * 1.5 };
  }

  private async loadGauges(center: LatLng, distance: number): Promise<void> {
    const visibleRegion = GaugeStore.visibleRegion(center, distance);
    const fetchRegion = scaledRegion(visibleRegion, GAUGE_CACHE.fetchPaddingMultiplier);
    const now = this.now();
    this.cachedRegions = this.cachedRegions.filter((r) => now - r.loadedAt <= GAUGE_CACHE.regionLifetimeMs);

    // Keep every previously loaded gauge available while fresh data loads.
    this.publishCachedGauges();

    try {
      if (!this.cachedRegions.some((r) => regionContainsRegion(r.region, visibleRegion))) {
        this.loadAbort = new AbortController();
        this.isLoadingRegion = true;
        this.notify();
        const fetched = await this.loadRegion(fetchRegion, this.loadAbort.signal);
        for (const gauge of fetched) {
          const cached = this.cachedGaugesByID.get(gauge.id);
          if (cached) cached.gauge = mergeStatus(gauge, cached.gauge);
          else this.cachedGaugesByID.set(gauge.id, { gauge, severityUpdatedAt: null, notFoundAt: null });
        }
        this.cachedRegions.push({ region: fetchRegion, loadedAt: now });
        this.isLoadingRegion = false;
        this.publishCachedGauges();
      }
      this.prioritizeSeverityLoads(visibleRegion, fetchRegion, now);
    } catch {
      // Preserve cached gauges when a refresh fails or is cancelled.
      this.isLoadingRegion = false;
      this.notify();
    }
  }

  private prioritizeSeverityLoads(visibleRegion: Region, fetchRegion: Region, now: number): void {
    const stale: Gauge[] = [];
    for (const cached of this.cachedGaugesByID.values()) {
      const p = { lat: cached.gauge.lat, lng: cached.gauge.lng };
      if (!regionContainsPoint(fetchRegion, p)) continue;
      if (cached.notFoundAt != null && now - cached.notFoundAt <= GAUGE_CACHE.notFoundLifetimeMs) continue;
      if (cached.severityUpdatedAt == null || now - cached.severityUpdatedAt > GAUGE_CACHE.severityLifetimeMs) {
        stale.push(cached.gauge);
      }
    }
    const center = visibleRegion.center;
    stale.sort((a, b) => {
      const av = regionContainsPoint(visibleRegion, { lat: a.lat, lng: a.lng });
      const bv = regionContainsPoint(visibleRegion, { lat: b.lat, lng: b.lng });
      if (av !== bv) return av ? -1 : 1;
      return distanceSquaredDeg({ lat: a.lat, lng: a.lng }, center) - distanceSquaredDeg({ lat: b.lat, lng: b.lng }, center);
    });
    // Replace queued work with the latest viewport's priorities; in-flight requests finish.
    this.pendingSeverityIDs = stale.map((g) => g.id).filter((id) => !this.severityTasksByID.has(id));
    this.startQueuedSeverityLoads();
  }

  private startQueuedSeverityLoads(): void {
    while (this.severityTasksByID.size < GAUGE_CACHE.maximumConcurrentSeverityLoads && this.pendingSeverityIDs.length) {
      const id = this.pendingSeverityIDs.shift()!;
      this.startSeverityLoad(id);
    }
  }

  private startSeverityLoad(id: string): void {
    const ac = new AbortController();
    this.severityTasksByID.set(id, ac);
    this.loadSeverity(id, ac.signal)
      .then((severity) => {
        if (ac.signal.aborted) return;
        const cached = this.cachedGaugesByID.get(id);
        if (!cached) return;
        cached.gauge = {
          ...cached.gauge,
          observationSeverity: severity.observed,
          forecastSeverity: severity.forecast,
          forecastPeakTime: severity.forecastPeakTime,
          forecastIsRising: severity.forecastIsRising,
          nextStage: severity.nextStage,
          nextStageTime: severity.nextStageTime,
        };
        cached.severityUpdatedAt = this.now();
        cached.notFoundAt = null;
        this.scheduleSeverityPublish();
      })
      .catch((err: unknown) => {
        // NWPS has no record for most USGS sites (404): remember that so the
        // site is not asked again on every camera move. Other failures are
        // retried by a later viewport request, as on iOS.
        if (err instanceof GaugeNotFoundError) {
          const cached = this.cachedGaugesByID.get(id);
          if (cached) cached.notFoundAt = this.now();
        }
      })
      .finally(() => {
        this.severityTasksByID.delete(id);
        this.startQueuedSeverityLoads();
      });
  }

  /** Batches nearby status responses so the map is not rebuilt per response. */
  private scheduleSeverityPublish(): void {
    if (this.publishTimer) return;
    this.publishTimer = setTimeout(() => {
      this.publishTimer = null;
      this.publishCachedGauges();
    }, GAUGE_CACHE.severityPublishBatchMs);
  }

  private publishCachedGauges(): void {
    const all = Array.from(this.cachedGaugesByID.values(), (c) => c.gauge);
    const culled = this.visibleRegion
      ? all.filter((g) => regionContainsPoint(paddedRegion(this.visibleRegion!, GAUGE_CACHE.visibleRegionPaddingFraction), { lat: g.lat, lng: g.lng }))
      : all;
    const now = new Date(this.now());
    this.visibleGauges = culled
      .map((g) => ({
        ...g,
        hoursToCrest: g.forecastPeakTime ? hoursFromNow(g.forecastPeakTime, now) : null,
        hoursToNextStage: g.nextStageTime ? hoursFromNow(g.nextStageTime, now) : null,
      }))
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    this.notify();
  }

  /** Test and debugging access. */
  get cachedCount(): number {
    return this.cachedGaugesByID.size;
  }

  get inFlightSeverityLoads(): number {
    return this.severityTasksByID.size;
  }
}

/** Gauge.mergingStatus(from:): a fresh ArcGIS record keeps the cached status fields. */
export function mergeStatus(fresh: Gauge, cached: Gauge): Gauge {
  return {
    ...fresh,
    observationSeverity: cached.observationSeverity,
    forecastSeverity: cached.forecastSeverity,
    forecastPeakTime: cached.forecastPeakTime,
    forecastIsRising: cached.forecastIsRising,
    nextStage: cached.nextStage,
    nextStageTime: cached.nextStageTime,
  };
}

export const gaugeStore = new GaugeStore();
